import { create } from "apisauce";
import { cacheImg, getCachedImg } from "./utils";
import { tabSessionId } from "./tabSession";

export const createApi = () => {
  const api = create({
    baseURL: import.meta.env.VITE_URL_API,
    headers: {
      "Cache-Control": "no-cache",
      // Sent on every request so the backend can tag its Sentry events with the same session id,
      // correlating frontend and backend errors from one tab/session (see tabSession.js).
      "x-tab-session": tabSessionId,
    },
    timeout: 300000,
    withCredentials: true,
  });

  // Security
  const login = (credentialResponse) => {
    return api.post(`/api/auth/login`, credentialResponse);
  };

  const logout = () => {
    return api.post(`/api/auth/logout`);
  };

  const validateToken = () => {
    return api.get(`/api/auth/validateToken`, {});
  };

  // User
  const getUsers = () => {
    return api.get(`/api/users`);
  };

  const getUserNames = () => {
    return api.get(`/api/users/names`);
  };

  const getUserActionLogs = (userId) => {
    return api.get(`/api/users/${userId}/action-logs`);
  };

  const saveUser = (user) => {
    return api.post(`/api/users/${user.id}`, user);
  };

  const createUser = (user) => {
    return api.post(`/api/users`, user);
  };

  const exportUsers = () => {
    return api.get(`/api/users/export`);
  };

  const savePinnedTabs = (tabs) => api.post(`/api/users/pinnedTabs`, { pinnedTabs: tabs });

  const saveVisibility = (visibility) => {
    return api.post(`/api/users/visibility`, visibility);
  };

  // Roles
  const getRoles = () => {
    return api.get(`/api/roles`);
  };

  const getRolesWithCount = () => {
    return api.get(`/api/roles/with-count`);
  };

  const editRole = (role) => {
    return api.post(`/api/roles/${role.id}`, role);
  };

  const getRolePrivilegeAuditLog = (roleId) => {
    return api.get(`/api/roles/${roleId}/privilegeAuditLog`);
  };

  const getUserRoleAuditLog = (hrCode) => {
    return api.get(`/api/users/${hrCode}/roleAuditLog`);
  };

  // Profile Image
  const getProfileImgThumb = (hrCode) => {
    let cached = getCachedImg(hrCode);
    if (cached) {
      return Promise.resolve({ status: 200, data: cached });
    }
    let response = api.get(`/api/profile-img/view-thumb/${hrCode}`, {}, { responseType: "blob" });
    response.then((res) => {
      if (res.status === 200) {
        cacheImg(hrCode, res.data);
      } else {
        cacheImg(hrCode, null);
      }
    });
    return response;
  };

  return {
    login,
    logout,

    // User
    validateToken,
    getUsers,
    getUserNames,
    saveUser,
    createUser,
    exportUsers,
    getUserActionLogs,
    savePinnedTabs,
    saveVisibility,

    // Roles
    getRoles,
    getRolesWithCount,
    editRole,
    getRolePrivilegeAuditLog,
    getUserRoleAuditLog,

    // Profile Image
    getProfileImgThumb,
  };
};

// Creates a singleton
export default createApi();
